import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2,
  AlertCircle,
  Flame,
  Copy,
  ExternalLink,
  BarChart3,
  Check,
  Plus,
  LogOut,
  Calendar,
  Target,
  Trash2,
  Eye,
  EyeOff,
  RotateCcw,
  ArrowLeft,
  Pencil,
  FlaskConical,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import QuestionEditor from "@/components/QuestionEditor";

/**
 * Check if we're in a preview/dev environment (NOT production).
 * Uses env var first, then hostname fallback.
 * This is a frontend guard - backend also blocks the endpoint in production.
 */
const getIsPreviewEnv = () => {
  // Check env var first (set at build time)
  const appEnv = process.env.REACT_APP_APP_ENV;
  if (appEnv) {
    return appEnv.toLowerCase() !== "production";
  }
  
  // Hostname fallback - check if running on preview infrastructure
  const host = window.location.hostname || "";
  return host.includes("preview") || host.includes("emergentagent.com") || host === "localhost";
};

const isPreviewEnv = getIsPreviewEnv();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyChallengesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allChallenges, setAllChallenges] = useState([]); // All challenges (active + archived)
  const [creator, setCreator] = useState(null);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [challengeToArchive, setChallengeToArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  
  // Creator Edit Mode state
  const [devLoginAvailable, setDevLoginAvailable] = useState(false);
  const [devLoggingIn, setDevLoggingIn] = useState(false);
  const [editingChallengeId, setEditingChallengeId] = useState(null);

  // Derive active and archived lists from allChallenges
  const activeChallenges = allChallenges.filter(c => !c.archived);
  const archivedChallenges = allChallenges.filter(c => c.archived);

  // Fetch all challenges (always include archived for the safety patch)
  const fetchAllChallenges = async () => {
    try {
      const res = await axios.get(`${API}/creators/me/challenges?include_archived=true`);
      if (res.data.success) {
        setAllChallenges(res.data.challenges);
      }
    } catch (err) {
      console.error("Error fetching challenges:", err);
    }
  };
  
  // Check if dev login is available (preview environment only)
  const checkDevLoginStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/dev-login-status`);
      setDevLoginAvailable(res.data.available === true);
    } catch (err) {
      // Not available - that's fine, means we're in production
      setDevLoginAvailable(false);
    }
  };
  
  // Handle dev login for testing
  const handleDevLogin = async (creatorKey = "creator_a") => {
    setDevLoggingIn(true);
    try {
      const res = await axios.post(`${API}/auth/dev-login?creator_key=${creatorKey}`);
      if (res.data.success) {
        localStorage.setItem("creator_profile", JSON.stringify(res.data.creator));
        setCreator(res.data.creator);
        toast.success(`Dev login: ${res.data.creator.name}`);
        // Refresh challenges for this dev user
        await fetchAllChallenges();
      }
    } catch (err) {
      console.error("Dev login error:", err);
      toast.error(err.response?.data?.detail || "Dev login failed");
    } finally {
      setDevLoggingIn(false);
    }
  };

  useEffect(() => {
    // Check dev login availability on mount (independent of auth state)
    checkDevLoginStatus();

    // Fetch creator profile and ALL challenges (auth via httpOnly cookie)
    const fetchData = async () => {
      try {
        const [profileRes, challengesRes] = await Promise.all([
          axios.get(`${API}/auth/me`),
          axios.get(`${API}/creators/me/challenges?include_archived=true`)
        ]);

        if (profileRes.data.success) {
          setCreator(profileRes.data.creator);
        }
        if (challengesRes.data.success) {
          setAllChallenges(challengesRes.data.challenges);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        if (err.response?.status === 401) {
          localStorage.removeItem("creator_profile");
          navigate("/?login=1");
        } else {
          setError("Failed to load your challenges");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleCopyChallengeLink = async (challengeId) => {
    const link = `${window.location.origin}/challenge/${challengeId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(challengeId);
      toast.success("Challenge link copied!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const handleOpenDashboard = async (challengeId) => {
    setLoadingDashboard(challengeId);
    
    try {
      const res = await axios.get(`${API}/creators/me/challenges/${challengeId}/dashboard-token`);
      
      if (res.data.success && res.data.dashboard_token) {
        window.open(`/dashboard/${res.data.dashboard_token}`, "_blank");
      }
    } catch (err) {
      toast.error("Failed to get dashboard link");
    } finally {
      setLoadingDashboard(null);
    }
  };

  const handleCopyDashboardLink = async (challengeId) => {
    try {
      const res = await axios.get(`${API}/creators/me/challenges/${challengeId}/dashboard-token`);
      
      if (res.data.success && res.data.dashboard_token) {
        const link = `${window.location.origin}/dashboard/${res.data.dashboard_token}`;
        await navigator.clipboard.writeText(link);
        toast.success("Dashboard link copied!");
      }
    } catch (err) {
      toast.error("Failed to copy dashboard link");
    }
  };

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    localStorage.removeItem("creator_profile");
    localStorage.removeItem("pending_claim_challenge_id");
    navigate("/");
    toast.success("Logged out");
  };

  const handleShowArchiveModal = (challenge) => {
    setChallengeToArchive(challenge);
    setArchiveModalOpen(true);
  };

  const handleArchiveChallenge = async () => {
    if (!challengeToArchive) return;
    
    setArchiving(true);
    
    try {
      await axios.delete(`${API}/creators/me/challenges/${challengeToArchive.challenge_id}`);
      
      // Update local state: mark challenge as archived instead of removing
      setAllChallenges(prev => prev.map(c => 
        c.challenge_id === challengeToArchive.challenge_id 
          ? { ...c, archived: true, archived_at: new Date().toISOString() }
          : c
      ));
      toast.success("Challenge removed.");
      setArchiveModalOpen(false);
      setChallengeToArchive(null);
    } catch (err) {
      console.error("Error archiving challenge:", err);
      toast.error(err.response?.data?.detail || "Failed to remove challenge");
    } finally {
      setArchiving(false);
    }
  };

  const handleRestoreChallenge = async (challengeId) => {
    setRestoringId(challengeId);
    
    try {
      const res = await axios.post(
        `${API}/creators/me/challenges/${challengeId}/restore`,
        {}
      );
      
      if (res.data.success && res.data.restored) {
        toast.success("Challenge restored.");
        // Update local state: mark challenge as not archived
        setAllChallenges(prev => prev.map(c => 
          c.challenge_id === challengeId 
            ? { ...c, archived: false, archived_at: null }
            : c
        ));
      } else {
        toast.info("Challenge was already active.");
      }
    } catch (err) {
      console.error("Error restoring challenge:", err);
      toast.error(err.response?.data?.detail || "Failed to restore challenge");
    } finally {
      setRestoringId(null);
    }
  };

  // Toggle is now just a UI state, not a refetch
  const handleToggleShowArchived = () => {
    setShowArchived(!showArchived);
  };

  const showTidyTip = activeChallenges.length > 25;

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 rounded-2xl">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 text-ember mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-2xl text-sage-900 mb-2">Loading...</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center py-8 px-4">
        <Card className="max-w-md w-full rounded-2xl">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-sage-900 mb-2">Oops!</h2>
            <p className="font-body text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate("/")} className="rounded-full">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper py-8 px-4" data-testid="my-challenges-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-light text-sage-900 flex items-center gap-3">
              <div className="p-2 bg-ember/10 rounded-xl">
                <Flame className="w-6 h-6 text-ember" />
              </div>
              My Challenges
            </h1>
            {creator && (
              <p className="font-body text-muted-foreground mt-1">
                {creator.email}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/")}
              className="rounded-full bg-ember hover:bg-ember/90"
              data-testid="create-new-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Challenge
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="rounded-full"
              data-testid="logout-btn"
              aria-label="Log out of your account"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Log out
            </Button>
          </div>
        </div>
        
        {/* Dev Login Section - Preview Environment Only (double-gated) */}
        {isPreviewEnv && devLoginAvailable && (
          <div className="mb-6 p-4 bg-violet-50 border border-violet-200 rounded-xl" data-testid="dev-login-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FlaskConical className="w-5 h-5 text-violet-600" />
                <div>
                  <p className="font-body text-sm font-medium text-violet-800">
                    Dev Login (Preview Only)
                  </p>
                  <p className="text-xs text-violet-600">
                    Test creator accounts for Creator Edit Mode
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleDevLogin("creator_a")}
                  variant="outline"
                  size="sm"
                  disabled={devLoggingIn}
                  className="rounded-full border-violet-300 text-violet-700 hover:bg-violet-100"
                  data-testid="dev-login-a-btn"
                >
                  {devLoggingIn ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Creator A"
                  )}
                </Button>
                <Button
                  onClick={() => handleDevLogin("creator_b")}
                  variant="outline"
                  size="sm"
                  disabled={devLoggingIn}
                  className="rounded-full border-violet-300 text-violet-700 hover:bg-violet-100"
                  data-testid="dev-login-b-btn"
                >
                  {devLoggingIn ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Creator B"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tidy tip for many challenges */}
        {showTidyTip && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="font-body text-sm text-amber-700">
              Tip: You can remove old challenges to keep your list tidy.
            </p>
          </div>
        )}

        {/* Show archived toggle - SAFETY PATCH: always visible when ANY challenges exist */}
        {(activeChallenges.length > 0 || archivedChallenges.length > 0) && (
          <div className="flex justify-between items-center mb-4">
            {/* Back to Active button - only show when viewing archived */}
            {showArchived ? (
              <Button
                onClick={handleToggleShowArchived}
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="back-to-active-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Back to Active
              </Button>
            ) : (
              <div /> 
            )}
            <Button
              onClick={handleToggleShowArchived}
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              data-testid="toggle-archived-btn"
            >
              {showArchived ? (
                <>
                  <EyeOff className="w-4 h-4 mr-1.5" />
                  Hide removed
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-1.5" />
                  Show removed
                </>
              )}
            </Button>
          </div>
        )}

        {/* Challenge List - determine what to display based on toggle state */}
        {activeChallenges.length === 0 && archivedChallenges.length === 0 ? (
          // No challenges at all
          <Card className="border-border bg-card rounded-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Flame className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="font-heading text-xl text-sage-900 mb-2">No challenges yet</h2>
              <p className="font-body text-muted-foreground mb-6">
                Create your first streak challenge and share it with your students.
              </p>
              <Button
                onClick={() => navigate("/")}
                className="rounded-full bg-ember hover:bg-ember/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Challenge
              </Button>
            </CardContent>
          </Card>
        ) : !showArchived && activeChallenges.length === 0 ? (
          // SAFETY PATCH: No active challenges but archived exist - show helpful message
          <Card className="border-border bg-card rounded-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Flame className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="font-heading text-xl text-sage-900 mb-2">No active challenges</h2>
              <p className="font-body text-muted-foreground mb-6">
                Turn on "Show removed" to view archived challenges.
              </p>
              <Button
                onClick={() => navigate("/")}
                className="rounded-full bg-ember hover:bg-ember/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Challenge
              </Button>
            </CardContent>
          </Card>
        ) : showArchived && archivedChallenges.length === 0 ? (
          // Showing archived but none exist
          <Card className="border-border bg-card rounded-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Flame className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="font-heading text-xl text-sage-900 mb-2">No removed challenges</h2>
              <p className="font-body text-muted-foreground">
                Challenges you remove will appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          // Display challenges based on toggle state
          <div className="space-y-4">
            {(showArchived ? archivedChallenges : activeChallenges).map((challenge) => (
              <Card 
                key={challenge.challenge_id} 
                className={`border-border bg-card rounded-2xl ${challenge.archived ? 'opacity-60' : ''}`}
                data-testid={`challenge-row-${challenge.challenge_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    {/* Challenge Info */}
                    <div className="flex-grow">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading text-lg text-sage-900">
                          {challenge.subject}
                        </h3>
                        <span className="px-2 py-0.5 bg-secondary rounded-full text-xs font-body text-muted-foreground">
                          {challenge.grade_level}
                        </span>
                        {challenge.archived && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs font-body">
                            Removed
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground font-body">
                        <span className="flex items-center gap-1">
                          <Target className="w-4 h-4" />
                          {challenge.streak_target} in a row
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(challenge.created_at).toLocaleDateString()}
                        </span>
                        <span>
                          {challenge.question_count || "30"} questions
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {/* Copy Challenge Link */}
                      <Button
                        onClick={() => handleCopyChallengeLink(challenge.challenge_id)}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        data-testid={`copy-link-${challenge.challenge_id}`}
                      >
                        {copiedId === challenge.challenge_id ? (
                          <>
                            <Check className="w-4 h-4 mr-1 text-green-500" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-1" />
                            Challenge Link
                          </>
                        )}
                      </Button>

                      {/* Dashboard Actions */}
                      <Button
                        onClick={() => handleOpenDashboard(challenge.challenge_id)}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        disabled={loadingDashboard === challenge.challenge_id}
                        data-testid={`open-dashboard-${challenge.challenge_id}`}
                      >
                        {loadingDashboard === challenge.challenge_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <BarChart3 className="w-4 h-4 mr-1" />
                            Dashboard
                          </>
                        )}
                      </Button>

                      <Button
                        onClick={() => handleCopyDashboardLink(challenge.challenge_id)}
                        variant="ghost"
                        size="icon"
                        className="rounded-full"
                        title="Copy dashboard link"
                        data-testid={`copy-dashboard-${challenge.challenge_id}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      
                      {/* Edit Questions button - only for non-archived challenges */}
                      {!challenge.archived && (
                        <Button
                          onClick={() => setEditingChallengeId(challenge.challenge_id)}
                          variant="outline"
                          size="sm"
                          className="rounded-full border-sage-500 text-sage-900 hover:bg-sage-500 hover:text-white hover:border-sage-500 focus:ring-2 focus:ring-sage-500 focus:ring-offset-2 transition-colors"
                          title="Edit questions in this challenge"
                          data-testid={`edit-questions-${challenge.challenge_id}`}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit Questions
                        </Button>
                      )}

                      {/* Restore button - only for archived challenges */}
                      {challenge.archived && (
                        <Button
                          onClick={() => handleRestoreChallenge(challenge.challenge_id)}
                          variant="outline"
                          size="sm"
                          className="rounded-full text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                          disabled={restoringId === challenge.challenge_id}
                          aria-label="Restore this challenge to your active list"
                          data-testid={`restore-${challenge.challenge_id}`}
                        >
                          {restoringId === challenge.challenge_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Restore
                            </>
                          )}
                        </Button>
                      )}

                      {/* Remove button - only for non-archived */}
                      {!challenge.archived && (
                        <Button
                          onClick={() => handleShowArchiveModal(challenge)}
                          variant="ghost"
                          size="sm"
                          className="rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-50"
                          title="Remove challenge"
                          aria-label="Remove this challenge from your list"
                          data-testid={`remove-${challenge.challenge_id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Stats Footer */}
        {(activeChallenges.length > 0 || archivedChallenges.length > 0) && (
          <div className="mt-8 text-center">
            <p className="font-body text-sm text-muted-foreground">
              {activeChallenges.length} active challenge{activeChallenges.length !== 1 ? "s" : ""}
              {archivedChallenges.length > 0 && (
                <span className="text-muted-foreground/60">
                  {" "}• {archivedChallenges.length} removed
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveModalOpen} onOpenChange={setArchiveModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-sage-900">
              Remove challenge?
            </DialogTitle>
            <DialogDescription className="font-body text-muted-foreground">
              This hides it from your list. Existing links will still work.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setArchiveModalOpen(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleArchiveChallenge}
              disabled={archiving}
              className="rounded-full bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-remove-btn"
            >
              {archiving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Remove"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Question Editor Modal */}
      {editingChallengeId && (
        <QuestionEditor
          challengeId={editingChallengeId}
          onClose={() => setEditingChallengeId(null)}
          onSaved={() => {
            // Optionally refresh challenges or show feedback
            fetchAllChallenges();
          }}
        />
      )}
    </div>
  );
}
